import React, {useState} from "react";
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {toast} from "react-toastify";
import {
    UserIcon,
    KeyIcon,
    ClipboardDocumentIcon,
    TrashIcon,
    PlusIcon,
    CheckIcon,
} from "@heroicons/react/20/solid";
import {
    getMCPUsers,
    createMCPUser,
    deleteMCPUser,
    changeMCPUserPassword,
    getMCPTokens,
    createMCPToken,
    revokeMCPToken,
} from "../../../api/mcp";
import {useAuth} from "../../../contexts/AuthContext";
import {PrimaryButton, SecondaryButton} from "../../elements/Buttons";
import BasicModal from "../../elements/modal";

function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
}

export default function TeamSettings() {
    const {currentUser} = useAuth();
    const queryClient = useQueryClient();

    // Modal states
    const [addUserModalOpen, setAddUserModalOpen] = useState(false);
    const [generateTokenModalOpen, setGenerateTokenModalOpen] = useState(false);
    const [deleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userToChangePassword, setUserToChangePassword] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [newToken, setNewToken] = useState(null);
    const [copiedToken, setCopiedToken] = useState(false);

    // Form states
    const [newUserForm, setNewUserForm] = useState({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        is_team_admin: false,
        generate_token: false,
    });
    const [tokenName, setTokenName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch users (admin only)
    const {data: users, isLoading: usersLoading} = useQuery(
        ['mcp_users', currentUser.id],
        getMCPUsers,
        {
            enabled: currentUser?.is_team_admin,
            refetchInterval: 1000 * 60 * 2
        }
    );

    // Fetch my tokens
    const {data: tokens, isLoading: tokensLoading} = useQuery(
        ['mcp_tokens', currentUser.id],
        getMCPTokens,
        {refetchInterval: 1000 * 60 * 2}
    );

    // Check if current user is admin (via groups check)
    const isAdmin = currentUser?.is_team_admin;

    // Add user handler
    async function handleAddUser(e) {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const result = await createMCPUser(newUserForm);
            toast.success('User created successfully');
            queryClient.invalidateQueries(['mcp_users']);

            if (result.token) {
                setNewToken(result.token);
                setAddUserModalOpen(false);
                setGenerateTokenModalOpen(true);
            } else {
                setAddUserModalOpen(false);
            }

            setNewUserForm({
                email: '',
                first_name: '',
                last_name: '',
                password: '',
                is_team_admin: false,
                generate_token: false,
            });
        } catch (error) {
            const message = error.response?.data?.email?.[0] ||
                          error.response?.data?.detail ||
                          'Failed to create user';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    }

    // Delete user handler
    async function handleDeleteUser() {
        if (!userToDelete) return;
        try {
            await deleteMCPUser(userToDelete.id);
            toast.success(`User ${userToDelete.email} deactivated`);
            queryClient.invalidateQueries(['mcp_users']);
            setDeleteUserModalOpen(false);
            setUserToDelete(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete user');
        }
    }

    // Change password handler
    async function handleChangePassword(e) {
        e.preventDefault();
        if (!userToChangePassword) return;
        setIsSubmitting(true);
        try {
            await changeMCPUserPassword(userToChangePassword.id, newPassword);
            toast.success(`Password updated for ${userToChangePassword.email}`);
            setPasswordModalOpen(false);
            setUserToChangePassword(null);
            setNewPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to change password');
        } finally {
            setIsSubmitting(false);
        }
    }

    // Generate token handler
    async function handleGenerateToken(e) {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const result = await createMCPToken({name: tokenName});
            setNewToken(result.token);
            setTokenName('');
            queryClient.invalidateQueries(['mcp_tokens']);
            toast.success('Token generated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to generate token');
        } finally {
            setIsSubmitting(false);
        }
    }

    // Revoke token handler
    async function handleRevokeToken(token) {
        try {
            await revokeMCPToken(token.id);
            toast.success(`Token "${token.name}" revoked`);
            queryClient.invalidateQueries(['mcp_tokens']);
        } catch (error) {
            toast.error('Failed to revoke token');
        }
    }

    // Copy token to clipboard
    async function handleCopyToken() {
        if (newToken) {
            await navigator.clipboard.writeText(newToken);
            setCopiedToken(true);
            toast.success('Token copied to clipboard');
            setTimeout(() => setCopiedToken(false), 2000);
        }
    }

    return (
        <div className="space-y-6">
            {/* My API Tokens Section */}
            <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                    <div className="sm:flex sm:items-center sm:justify-between">
                        <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                                API Tokens
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Manage your API tokens for Claude Code / Cowork integration.
                            </p>
                        </div>
                        <div className="mt-4 sm:mt-0">
                            <PrimaryButton
                                label="Generate Token"
                                onClick={() => setGenerateTokenModalOpen(true)}
                                icon={<KeyIcon className="h-4 w-4 mr-1" />}
                            />
                        </div>
                    </div>

                    {tokensLoading ? (
                        <div className="mt-6 text-center text-gray-500">Loading tokens...</div>
                    ) : tokens && tokens.length > 0 ? (
                        <div className="mt-6">
                            <table className="min-w-full divide-y divide-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                                            Name
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Created
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Last Used
                                        </th>
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white">
                                    {tokens.map((token, index) => (
                                        <tr key={token.id} className={index % 2 === 0 ? undefined : 'bg-gray-50'}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                                {token.name}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {new Date(token.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                {token.last_used_at
                                                    ? new Date(token.last_used_at).toLocaleString()
                                                    : 'Never'}
                                            </td>
                                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRevokeToken(token)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    Revoke
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="mt-6 text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                            <KeyIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No API tokens</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Generate a token to use with Claude Code or Cowork.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Team Members Section (Admin only) */}
            {isAdmin && (
                <div className="bg-white shadow sm:rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <div className="sm:flex sm:items-center sm:justify-between">
                            <div>
                                <h3 className="text-lg font-medium leading-6 text-gray-900">
                                    Team Members
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Manage users who can access the Support Brain API.
                                </p>
                            </div>
                            <div className="mt-4 sm:mt-0">
                                <PrimaryButton
                                    label="Add User"
                                    onClick={() => setAddUserModalOpen(true)}
                                    icon={<PlusIcon className="h-4 w-4 mr-1" />}
                                />
                            </div>
                        </div>

                        {usersLoading ? (
                            <div className="mt-6 text-center text-gray-500">Loading users...</div>
                        ) : users && users.length > 0 ? (
                            <div className="mt-6 overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                                                User
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Role
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Last Login
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Tokens
                                            </th>
                                            <th scope="col" className="relative py-3.5 pl-3 pr-4">
                                                <span className="sr-only">Actions</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {users.map((user, index) => (
                                            <tr key={user.id} className={index % 2 === 0 ? undefined : 'bg-gray-50'}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                                                    <div className="flex items-center">
                                                        <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {user.first_name} {user.last_name}
                                                            </div>
                                                            <div className="text-gray-500">{user.email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {user.is_team_admin ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                                                            Admin
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-500">Member</span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {user.last_login
                                                        ? new Date(user.last_login).toLocaleDateString()
                                                        : 'Never'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {user.has_active_tokens ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">None</span>
                                                    )}
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm">
                                                    <div className="flex items-center justify-end space-x-2">
                                                        {user.id !== currentUser.id && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setUserToChangePassword(user);
                                                                        setPasswordModalOpen(true);
                                                                    }}
                                                                    className="text-gray-600 hover:text-gray-900"
                                                                    title="Change password"
                                                                >
                                                                    <KeyIcon className="h-5 w-5" />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setUserToDelete(user);
                                                                        setDeleteUserModalOpen(true);
                                                                    }}
                                                                    className="text-red-600 hover:text-red-900"
                                                                    title="Deactivate user"
                                                                >
                                                                    <TrashIcon className="h-5 w-5" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="mt-6 text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    Add users to give them API access.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            <BasicModal open={addUserModalOpen} setOpen={setAddUserModalOpen}>
                <form onSubmit={handleAddUser}>
                    <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                                Add User
                            </h3>
                            <div className="mt-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        value={newUserForm.email}
                                        onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            First Name
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            value={newUserForm.first_name}
                                            onChange={(e) => setNewUserForm({...newUserForm, first_name: e.target.value})}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            value={newUserForm.last_name}
                                            onChange={(e) => setNewUserForm({...newUserForm, last_name: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Password
                                    </label>
                                    <input
                                        type="password"
                                        required
                                        minLength={8}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        value={newUserForm.password}
                                        onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                                    />
                                </div>
                                <div className="flex items-center">
                                    <input
                                        id="is_team_admin"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={newUserForm.is_team_admin}
                                        onChange={(e) => setNewUserForm({...newUserForm, is_team_admin: e.target.checked})}
                                    />
                                    <label htmlFor="is_team_admin" className="ml-2 block text-sm text-gray-900">
                                        Make team admin
                                    </label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        id="generate_token"
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                        checked={newUserForm.generate_token}
                                        onChange={(e) => setNewUserForm({...newUserForm, generate_token: e.target.checked})}
                                    />
                                    <label htmlFor="generate_token" className="ml-2 block text-sm text-gray-900">
                                        Generate API token
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create User'}
                        </button>
                        <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                            onClick={() => setAddUserModalOpen(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </BasicModal>

            {/* Generate Token Modal */}
            <BasicModal open={generateTokenModalOpen} setOpen={(open) => {
                setGenerateTokenModalOpen(open);
                if (!open) setNewToken(null);
            }}>
                {newToken ? (
                    <div>
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">
                                    Token Generated
                                </h3>
                                <div className="mt-4">
                                    <p className="text-sm text-red-600 font-medium mb-2">
                                        Copy this token now. You won't be able to see it again.
                                    </p>
                                    <div className="flex items-center space-x-2">
                                        <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm font-mono break-all">
                                            {newToken}
                                        </code>
                                        <button
                                            type="button"
                                            onClick={handleCopyToken}
                                            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                                        >
                                            {copiedToken ? (
                                                <CheckIcon className="h-5 w-5 text-green-500" />
                                            ) : (
                                                <ClipboardDocumentIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="button"
                                className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto sm:text-sm"
                                onClick={() => {
                                    setGenerateTokenModalOpen(false);
                                    setNewToken(null);
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleGenerateToken}>
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">
                                    Generate API Token
                                </h3>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Token Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        placeholder="e.g., Claude Code - MacBook"
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        value={tokenName}
                                        onChange={(e) => setTokenName(e.target.value)}
                                    />
                                    <p className="mt-1 text-sm text-gray-500">
                                        Give your token a descriptive name so you can identify it later.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                            >
                                {isSubmitting ? 'Generating...' : 'Generate'}
                            </button>
                            <button
                                type="button"
                                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                                onClick={() => setGenerateTokenModalOpen(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </BasicModal>

            {/* Delete User Confirmation Modal */}
            <BasicModal open={deleteUserModalOpen} setOpen={setDeleteUserModalOpen}>
                <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left">
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                            Deactivate User
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm text-gray-500">
                                Are you sure you want to deactivate <strong>{userToDelete?.email}</strong>?
                                This will revoke all their API tokens and prevent them from accessing the system.
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={handleDeleteUser}
                    >
                        Deactivate
                    </button>
                    <button
                        type="button"
                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={() => setDeleteUserModalOpen(false)}
                    >
                        Cancel
                    </button>
                </div>
            </BasicModal>

            {/* Change Password Modal */}
            <BasicModal open={passwordModalOpen} setOpen={(open) => {
                setPasswordModalOpen(open);
                if (!open) {
                    setUserToChangePassword(null);
                    setNewPassword('');
                }
            }}>
                <form onSubmit={handleChangePassword}>
                    <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                                Change Password
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                                Set a new password for <strong>{userToChangePassword?.email}</strong>
                            </p>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    required
                                    minLength={8}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Minimum 8 characters"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Change Password'}
                        </button>
                        <button
                            type="button"
                            className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                            onClick={() => setPasswordModalOpen(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </BasicModal>
        </div>
    );
}
